import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BsbValidationResponse } from '@app/core/models/payment/bsb-validation.interface';
import { PostDepositPaymentResponseData } from '@app/core/models/payment/deposit.interface';
import { PostPayInFullPaymentResponseData } from '@app/core/models/payment/pay-in-full.interface';
import { Progress } from '@app/core/models/payment/progress.interface';
import { HelperService } from '@app/core/services/helper.service';
import { CommentPropertyName } from '@app/provider/pages/payments/modals/comment-modal/comment-modal.component';
import {
  PostConfirmOtpRequest,
  PostConfirmOtpResponse,
  PostSendOtpRequest
} from '@app/rp/sms-journey/contact-verification/contact-verification.interface';
import {
  PutPendingPlanResponse,
  PutPlanPaymentMethodResponse
} from '@app/rp/sms-journey/interface/rp-sms-journey.interface';
import { environment } from '@environments/environment';
import { NGX_LOADING_BAR_IGNORED } from '@ngx-loading-bar/http-client';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  CybersourceData,
  ErrorResponse,
  GenericListResponse,
  GenericModelResponse,
  GetSmsJourneyPlan,
  KycPayload,
  OtpResponse,
  PaymentMissedItemResponse,
  PaymentPlanItemResponse,
  UnsubmittedPlan,
  UpdatePlanByFieldCodeRequestPayload
} from '../../models';
import { ApiUrlService } from '../../services/api-url.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentPlanDataService {
  constructor(
    public httpClient: HttpClient,
    public apiUrlService: ApiUrlService,
    private helperService: HelperService
  ) {}

  getPaymentPlanUnSubmitted$(options: {
    showLoadingBar?: boolean;
    includeExpiredPlans?: boolean;
  }): Observable<GenericListResponse<UnsubmittedPlan, ErrorResponse>> {
    return this.httpClient.get<GenericListResponse<UnsubmittedPlan, ErrorResponse>>(
      `${this.apiUrlService.savedPlanUrl}`,
      {
        context: new HttpContext().set(NGX_LOADING_BAR_IGNORED, !options.showLoadingBar),
        params: { includeExpiredPlans: options.includeExpiredPlans ? 'true' : 'false' }
      }
    );
  }

  getUnsubmittedPlanById$(
    id: string,
    toSkipOtpMaxAttempt = false
  ): Observable<GenericModelResponse<GetSmsJourneyPlan, ErrorResponse>> {
    return this.httpClient.get<GenericModelResponse<GetSmsJourneyPlan, ErrorResponse>>(
      `${this.apiUrlService.savedPlanUrl}/${id}?toSkipOtpMaxAttempt=${toSkipOtpMaxAttempt}`
    );
  }

  putUpdatePaymentPlan$(id: string, body: any): Observable<any> {
    return this.httpClient.put<any>(`${this.apiUrlService.updatePaymentPlanUrl}/${id}`, body);
  }

  deleteUnsubmittedPlanById$(id: string): Observable<any> {
    return this.httpClient.delete(this.apiUrlService.savedPlanUrl + `/${id}`).pipe();
  }

  getApprovalQueue$(): Observable<GenericListResponse<PaymentPlanItemResponse, ErrorResponse>> {
    return this.httpClient.get<GenericListResponse<PaymentPlanItemResponse, ErrorResponse>>(
      `${this.apiUrlService.paymentPlanSummaryUrl}?moduleType=getPendingApprovalDdrList`,
      {
        context: new HttpContext().set(NGX_LOADING_BAR_IGNORED, true)
      }
    );
  }

  downloadAgreement(key: string): Observable<GenericModelResponse<string, HttpErrorResponse>> {
    return this.httpClient
      .get<GenericModelResponse<string, HttpErrorResponse>>(
        this.apiUrlService.paymentPlanSummaryUrl + `/${key}/download-agreement`
      )
      .pipe(filter(response => !!response?.data));
  }

  downloadSignedAgreement$(pendingPlanId: string): Observable<string> {
    return this.httpClient
      .get<GenericListResponse<{ base64PdfString: string }, ErrorResponse>>(
        environment.api + `/payment-plan/treatment-agreement/${pendingPlanId}/download`
      )
      .pipe(
        filter(response => this.helperService.checkAPIResponse(response)),
        map(response => response.data[0].base64PdfString)
      );
  }

  sendAppLink(plan: any): Observable<any> {
    return this.httpClient.post(this.apiUrlService.sendAppLinkUrl, plan);
  }

  postSmsJourneyPreviewProposalPdf(payload: any, ddrDraftId: string): Observable<any> {
    return this.httpClient
      .post<any>(`${this.apiUrlService.postSmsJourneyPreviewProposalPdf}/${ddrDraftId}`, payload)
      .pipe(
        map(response => {
          return response.base64PdfString;
        })
      );
  }

  postSubmitPendingPlan(plan: any, urlParams: any): Observable<any> {
    return this.httpClient.post(this.apiUrlService.savedPlanNoLoginUrl + '/' + urlParams, plan);
  }

  postCreateEditPendingPlan(body: PostPendingPlanRequest): Observable<PostPendingPlanResponse> {
    return this.httpClient
      .post<{ data: any; error: any[]; isError: string }>(
        `${this.apiUrlService.savedPlanUrl}`,
        body
      )
      .pipe(
        map(response => {
          return response?.data;
        })
      );
  }

  updatePendingPlan(
    plan: GetSmsJourneyPlan,
    ddrDraftId: string
  ): Observable<PutPendingPlanResponse> {
    return this.httpClient.put<PutPendingPlanResponse>(
      `${this.apiUrlService.savedPlanNoLoginUrl}/${ddrDraftId}`,
      plan
    );
  }

  updatePlanPaymentMethod$(
    plan: GetSmsJourneyPlan,
    ddrDraftId: string
  ): Observable<PutPlanPaymentMethodResponse> {
    return this.httpClient.put<PutPlanPaymentMethodResponse>(
      `${this.apiUrlService.savedPlanNoLoginUrl}/${ddrDraftId}`,
      plan
    );
  }

  getDepositInvoiceFile(ddrId: string): Observable<any> {
    return this.httpClient.post<any>(`${this.apiUrlService.getDepositInvoice}`, { ddrID: ddrId });
  }

  sendOTP(ddrDraftId: string, payload: PostSendOtpRequest): Observable<OtpResponse[]> {
    // declare api endpoint with param link
    const apiLink = `${this.apiUrlService.sendOTPUrl}/${ddrDraftId}`;
    return this.httpClient.post(apiLink, payload).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  resendOTP(ddrDraftId: string, payload: PostSendOtpRequest): Observable<OtpResponse[]> {
    // declare api endpoint with param link
    const apiLink = `${this.apiUrlService.resendOTPUrl}/${ddrDraftId}`;
    return this.httpClient.post(apiLink, payload).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  confirmOtp(
    ddrDraftId: string,
    payload: PostConfirmOtpRequest
  ): Observable<PostConfirmOtpResponse> {
    const apiLink = `${this.apiUrlService.confirmOTPUrl}/${ddrDraftId}`;
    return this.httpClient
      .post<{ data: PostConfirmOtpResponse; error: any[]; isError: string }>(apiLink, payload)
      .pipe(
        map(response => {
          return response.data;
        })
      );
  }

  verifyKyc(ddrDraftId: string, payload: KycPayload): Observable<PostVerifyKycResponse> {
    const apiLink = `${this.apiUrlService.verifyKYC}/${ddrDraftId}`;

    return this.httpClient
      .post<{ data: PostVerifyKycResponse; error: any[]; isError: string }>(apiLink, payload)
      .pipe(
        map(response => {
          return response.data;
        })
      );
  }

  checkBsb(ddrDraftId: string, bsbNumber: string): Observable<BsbValidationResponse[]> {
    const apiLink = `${this.apiUrlService.bsbCheckNoLoginUrl}/${ddrDraftId}`;

    return this.httpClient
      .post<{ data: BsbValidationResponse[]; error: any[]; isError: string }>(apiLink, {
        bsbNumber
      })
      .pipe(
        map(response => {
          return response.data;
        })
      );
  }

  processDepositPayment$(
    ddrDraftId: string,
    payload: any
  ): Observable<PostDepositPaymentResponseData | null> {
    return this.httpClient
      .post<{
        data: any;
        error: any[];
        isError: string;
      }>(`${this.apiUrlService.postProcessPayment}/${ddrDraftId}`, payload)
      .pipe(
        map(response => {
          if (response?.data?.depositCCdetails) {
            return response.data as PostDepositPaymentResponseData;
          }
          return null;
        })
      );
  }

  getPlanProgressStep(ddrDraftId: string): Observable<Progress[]> {
    return this.httpClient
      .get<{ data: Progress[]; error: any[]; isError: string }>(
        `${this.apiUrlService.getPlanProgressStep}/${ddrDraftId}`
      )
      .pipe(
        map(response => {
          return response?.data;
        })
      );
  }

  processPayInFullPayment$(
    ddrDraftId: string,
    payload: any
  ): Observable<PostPayInFullPaymentResponseData | null> {
    return this.httpClient
      .post<{
        data: any;
        error: any[];
        isError: string;
      }>(`${this.apiUrlService.postProcessPayment}/${ddrDraftId}`, payload)
      .pipe(map(response => response.data));
  }

  getPaymentPlanMissed$(): Observable<{
    isError: string;
    readonly data: PaymentMissedItemResponse[];
    readonly errors: ErrorResponse[];
  }> {
    return this.httpClient.get<{
      isError: string;
      readonly data: PaymentMissedItemResponse[];
      readonly errors: ErrorResponse[];
    }>(`${this.apiUrlService.missedPaymentsUrl}`, {
      context: new HttpContext().set(NGX_LOADING_BAR_IGNORED, true)
    });
  }

  saveComment$(id: string, body: any, module: CommentPropertyName) {
    let url = '';
    if (module === CommentPropertyName.DDR_PLANS) {
      url = this.apiUrlService.updatePaymentPlanUrl;
    } else {
      url = this.apiUrlService.updateSavedPlanUrl;
    }

    return this.httpClient.put<any>(`${url}/${id}`, body).pipe(
      map(response => {
        return response?.data;
      })
    );
  }

  markAsPaymentCollectedInPractice$(
    ddrId: string,
    payload: UpdatePlanByFieldCodeRequestPayload
  ): Observable<any> {
    return this.httpClient
      .put<any>(`${this.apiUrlService.updatePaymentPlanUrl}/${ddrId}`, payload)
      .pipe(map(res => res.data));
  }

  cancelPifPayAtPractice$(ddrId: string): Observable<any> {
    return this.httpClient.post<any>(`${this.apiUrlService.cancelPifPayAtPracticeUrl}/${ddrId}`, {});
  }
}

export interface PostPendingPlanRequest {
  confim_details_cbx: boolean | null;
  custrecord_ddrip_proactive: boolean;
  custrecord_mfa_ddr_copy_rp_fields: boolean;
  custrecord_mfa_ddr_patient_dob: string;
  custrecord_mfa_ddr_single_payments: number;
  custrecord_mfa_ddr_term_length: string;
  custrecord_override_term_length: boolean;
  custrecord_pa_overwrite_payment_amount: number;
  ddr_source: string;
  deposit: number;
  doctor_id: string;
  no_of_payments: number;
  ortho_items: string[];
  patient_firstname: string;
  patient_lastname: string;
  patient_title: string;
  payment_freq: string;
  payment_plan_total: number;
  plan_type: string;
  practice_id: string;
  practice_patient_id_no: string;
  quote: PaymentPlanQuote[];
  reset_plan_progress: boolean;
  rp_dob: string;
  rp_firstname: string;
  rp_lastname: string;
  rp_mobile_num: string;
  rp_mobile_num2: MobileNumber;
  rp_title: string;
  rp_email: string;
  start_date: string;
  template: string;
  total_plan_value: string;
  treatment_type: string;
  custrecord_ddrip_old_saved_plan_id: string;
  prev_ddr: string;
  is_valid: boolean; // For checking in pending plans action menu
  pay_deposit_to?: string;
  custrecord_ddrip_send_sms_reminder: boolean;

  // Invoice
  invoice_number?: string;
  invoice_amount?: number;
}

interface MobileNumber {
  number: string;
  internationalNumber: string;
  nationalNumber: string;
  e164Number: string;
  countryCode: string;
  dialCode: string;
}

interface PaymentPlanQuote {
  term_weeks?: string;
  term_months: number;
  weekly: number;
  fortnightly: number;
  monthly: number;
}

export interface PostPendingPlanResponse {
  ddr: number;
  ddr_plan_type_final: string;
}

export interface GetSendSmsResponse {
  0: GetSendSmsDataResponse;
  key: string;
  resumeUrl: string;
  signedUrl: string;
}

interface GetSendSmsDataResponse {
  field: string;
  message: string;
}

export interface PostVerifyKycResponse {
  custrecord_ddrip_greenid_overall_status: string;
  custrecord_ddrip_greenid_verification_id: string;
  message: string;
  reset_rp_name: boolean;
  remainingRetry?: number;
  new_progress_steps: {
    data: Progress[];
  };
}

interface PostPayInFullRequestPayload {
  pifAmount: string;
  planType: string;
}

export interface PostPayInFullPaymentViaCybersourceRequest extends PostPayInFullRequestPayload {
  cybersource: CybersourceData;
}

export interface PostPayInFullPaymentViaWindcaveRequest extends PostPayInFullRequestPayload {
  ccNumber: string;
  ccExpireDate: string;
  ccName: string;
  ccPaymentMethod: string;
}

export interface PostProcessCcPaymentResponse {
  amount: string;
  ccnumber: string;
  date: string;
  tranid: string;
  salesorderid: number;
  ccapproved: boolean;
  message: string; // For failed payment
}
